import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, FileText, Activity, Search } from 'lucide-react';

export default function AdminAuditPage() {
  const { lang } = useApp();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => { api.get('/admin/audit-log?limit=200').then(r => setRows(r.data)); }, []);

  const filtered = rows.filter(r => {
    if (!q) return true;
    const v = q.toLowerCase();
    return (r.action || '').toLowerCase().includes(v) || (r.actor_email || '').toLowerCase().includes(v) || JSON.stringify(r.meta || {}).toLowerCase().includes(v);
  });

  const actionColor = (act) => {
    if (!act) return 'bg-secondary text-muted-foreground';
    if (act.includes('deleted')) return 'bg-rose-50 text-rose-700 border-rose-200';
    if (act.includes('created') || act.includes('added')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (act.includes('role')) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-secondary text-muted-foreground';
  };

  return (
    <main id="main" className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-audit-page">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/admin"><ArrowLeft className={`w-4 h-4 me-1 ${lang === 'ar' ? 'rotate-180' : ''}`} />{lang === 'ar' ? 'لوحة الإدارة' : 'Admin'}</Link>
      </Button>

      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-rose-100 grid place-items-center"><FileText className="w-6 h-6 text-rose-600" /></div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-black">{lang === 'ar' ? 'سجلّ النظام' : 'Audit log'}</h1>
          <p className="text-xs text-muted-foreground">{lang === 'ar' ? 'تتبّع كل إجراء إداري — تغيير الأدوار، إنشاء/حذف المراكز، حذف المستخدمين.' : 'Track every admin action — role changes, center CRUD, user deletion.'}</p>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === 'ar' ? 'بحث (إجراء، بريد، تفاصيل)...' : 'Search (action, email, details)...'} className="ps-10" data-testid="audit-search" />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base"><Activity className="w-4 h-4 inline me-1.5 text-rose-600" />{filtered.length} {lang === 'ar' ? 'إجراء' : 'entries'}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">{lang === 'ar' ? 'لا إجراءات' : 'No actions'}</p>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map(r => (
                <li key={r.id} className="p-4 hover:bg-secondary/30" data-testid={`audit-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <Badge variant="outline" className={`text-[10px] font-mono shrink-0 ${actionColor(r.action)}`}>{r.action}</Badge>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold line-clamp-1">{r.actor_email} <span className="text-muted-foreground font-normal">· {r.actor_role}</span></div>
                        {r.meta && Object.keys(r.meta).length > 0 && (
                          <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">{JSON.stringify(r.meta)}</div>
                        )}
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">{new Date(r.created_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-US', { dateStyle: 'short', timeStyle: 'short' })}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
